import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import {
  setPricePerDozen,
  setRetailerPriceUnder50,
  setRetailerPrice50Plus,
  removeDeal,
  removeDeliveryZone,
  removeDeliveryRange
} from '../store/slices/pricingSlice';
import { addToast } from '../store/slices/toastsSlice';
import CreateDealModal from '../components/CreateDealModal';
import CreateZoneModal from '../components/CreateZoneModal';
import CreateRangeModal from '../components/CreateRangeModal';

export const Pricing: React.FC = () => {
  const dispatch = useDispatch();
  const pricePerDozen = useSelector((state: RootState) => state.pricing.pricePerDozen);
  const retailerPriceUnder50 = useSelector((state: RootState) => state.pricing.retailerPriceUnder50);
  const retailerPrice50Plus = useSelector((state: RootState) => state.pricing.retailerPrice50Plus);
  const deals = useSelector((state: RootState) => state.pricing.deals);
  const deliveryZones = useSelector((state: RootState) => state.pricing.deliveryZones);
  const deliveryRanges = useSelector((state: RootState) => state.pricing.deliveryRanges);

  const [inputValue, setInputValue] = useState(pricePerDozen.toFixed(2));
  const [retailerUnder50Input, setRetailerUnder50Input] = useState(retailerPriceUnder50.toFixed(2));
  const [retailer50PlusInput, setRetailer50PlusInput] = useState(retailerPrice50Plus.toFixed(2));
  const [showDealModal, setShowDealModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [showRangeModal, setShowRangeModal] = useState(false);

  const handleDeleteDeal = (dealId: string) => {
    dispatch(removeDeal(dealId));
    dispatch(addToast('Deal deleted successfully!', 'success'));
  };

  const handleDeleteZone = (zoneId: string) => {
    dispatch(removeDeliveryZone(zoneId));
    dispatch(addToast('Delivery zone deleted successfully!', 'success'));
  };

  const handleDeleteRange = (rangeId: string) => {
    dispatch(removeDeliveryRange(rangeId));
    dispatch(addToast('Delivery range deleted successfully!', 'success'));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string, digits, and one decimal point with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleSave = () => {
    const parsedValue = parseFloat(inputValue);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      dispatch(setPricePerDozen(parsedValue));
      setInputValue(parsedValue.toFixed(2));
      dispatch(addToast('Price per dozen saved successfully!', 'success'));
    } else {
      dispatch(addToast('Please enter a valid price', 'error'));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  // Handlers for retailer price under 50
  const handleRetailerUnder50Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setRetailerUnder50Input(value);
    }
  };

  const handleRetailerUnder50Save = () => {
    const parsedValue = parseFloat(retailerUnder50Input);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      dispatch(setRetailerPriceUnder50(parsedValue));
      setRetailerUnder50Input(parsedValue.toFixed(2));
      dispatch(addToast('Retailer price for orders < 50 units saved successfully!', 'success'));
    } else {
      dispatch(addToast('Please enter a valid price', 'error'));
    }
  };

  const handleRetailerUnder50KeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleRetailerUnder50Save();
    }
  };

  // Handlers for retailer price 50+
  const handleRetailer50PlusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setRetailer50PlusInput(value);
    }
  };

  const handleRetailer50PlusSave = () => {
    const parsedValue = parseFloat(retailer50PlusInput);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      dispatch(setRetailerPrice50Plus(parsedValue));
      setRetailer50PlusInput(parsedValue.toFixed(2));
      dispatch(addToast('Retailer price for orders ≥ 50 units saved successfully!', 'success'));
    } else {
      dispatch(addToast('Please enter a valid price', 'error'));
    }
  };

  const handleRetailer50PlusKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleRetailer50PlusSave();
    }
  };

  return (
    <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#333' }}>Pricing</h1>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '8px' }}>Manage your pricing and deals</p>
      </header>

      {/* Price Per Dozen Section */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', color: '#333', marginBottom: '20px' }}>Price Per Dozen</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <label htmlFor="pricePerDozen" style={{ fontSize: '1.1rem', color: '#555', fontWeight: '500' }}>
            Price Per Dozen:
          </label>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                fontSize: '1rem'
              }}
            >
              $
            </span>
            <input
              id="pricePerDozen"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              style={{
                padding: '8px 12px 8px 28px',
                fontSize: '1rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '150px'
              }}
              placeholder="0.00"
            />
          </div>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 20px',
              fontSize: '1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
            onMouseOver={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
            onMouseOut={e => (e.currentTarget.style.backgroundColor = '#007bff')}
          >
            Save
          </button>
        </div>
        <div style={{ marginTop: '15px', color: '#666', fontSize: '0.95rem' }}>
          Current price: ${pricePerDozen.toFixed(2)}
        </div>
      </section>

      {/* For Retailers Section */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', color: '#333', marginBottom: '20px' }}>For Retailers</h2>

        {/* Retailer Price for orders < 50 units */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <label htmlFor="retailerPriceUnder50" style={{ fontSize: '1.1rem', color: '#555', fontWeight: '500' }}>
              Price for orders &lt; 50 units:
            </label>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  fontSize: '1rem'
                }}
              >
                $
              </span>
              <input
                id="retailerPriceUnder50"
                type="text"
                value={retailerUnder50Input}
                onChange={handleRetailerUnder50Change}
                onKeyDown={handleRetailerUnder50KeyDown}
                style={{
                  padding: '8px 12px 8px 28px',
                  fontSize: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '150px'
                }}
                placeholder="0.00"
              />
            </div>
            <button
              onClick={handleRetailerUnder50Save}
              style={{
                padding: '8px 20px',
                fontSize: '1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = '#007bff')}
            >
              Save
            </button>
          </div>
          <div style={{ marginTop: '15px', color: '#666', fontSize: '0.95rem' }}>
            Current price: ${retailerPriceUnder50.toFixed(2)}
          </div>
        </div>

        {/* Retailer Price for orders ≥ 50 units */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <label htmlFor="retailerPrice50Plus" style={{ fontSize: '1.1rem', color: '#555', fontWeight: '500' }}>
              Price for orders ≥ 50 units:
            </label>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  fontSize: '1rem'
                }}
              >
                $
              </span>
              <input
                id="retailerPrice50Plus"
                type="text"
                value={retailer50PlusInput}
                onChange={handleRetailer50PlusChange}
                onKeyDown={handleRetailer50PlusKeyDown}
                style={{
                  padding: '8px 12px 8px 28px',
                  fontSize: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '150px'
                }}
                placeholder="0.00"
              />
            </div>
            <button
              onClick={handleRetailer50PlusSave}
              style={{
                padding: '8px 20px',
                fontSize: '1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = '#007bff')}
            >
              Save
            </button>
          </div>
          <div style={{ marginTop: '15px', color: '#666', fontSize: '0.95rem' }}>
            Current price: ${retailerPrice50Plus.toFixed(2)}
          </div>
        </div>
      </section>

      {/* Deals Section */}
      <section style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '2rem', color: '#333', margin: 0 }}>Deals</h2>
          <button
            onClick={() => setShowDealModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            + Create New Deal
          </button>
        </div>

        {/* Deals List */}
        {deals.length === 0 ? (
          <p style={{ color: '#666', fontSize: '1rem', fontStyle: 'italic' }}>
            No deals created yet. Click "Create New Deal" to add one.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {deals.map(deal => (
              <div
                key={deal.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px 20px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px'
                }}
              >
                <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Quantity</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333' }}>{deal.quantity}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Price</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#4CAF50' }}>
                      ${deal.price.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Date Range</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '500', color: '#333' }}>
                      {new Date(deal.startDate).toLocaleDateString()} - {new Date(deal.endDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteDeal(deal.id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                  onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                  onMouseOut={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Delivery Zones Section */}
      <section style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '2rem', color: '#333', margin: 0 }}>Delivery Zones</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowZoneModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              + Create New Zone
            </button>
            <button
              onClick={() => setShowRangeModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              + Create New Range
            </button>
          </div>
        </div>

        {/* Delivery Zones List */}
        {deliveryZones.length === 0 ? (
          <p style={{ color: '#666', fontSize: '1rem', fontStyle: 'italic' }}>
            No delivery zones created yet. Click "Create New Zone" to add one.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {deliveryZones.map(zone => (
              <div
                key={zone.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px 20px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px'
                }}
              >
                <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Zone</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>{zone.towns.join(', ')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Delivery Price</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#4CAF50' }}>
                      ${zone.deliveryPrice.toFixed(2)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteZone(zone.id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                  onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                  onMouseOut={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Delivery Ranges List */}
        <div style={{ marginTop: '40px' }}>
          <h3 style={{ fontSize: '1.5rem', color: '#333', marginBottom: '20px' }}>Delivery Ranges</h3>
          {deliveryRanges.length === 0 ? (
            <p style={{ color: '#666', fontSize: '1rem', fontStyle: 'italic' }}>
              No delivery ranges created yet. Click "Create New Range" to add one.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {deliveryRanges.map(range => (
                <div
                  key={range.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '15px 20px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px'
                  }}
                >
                  <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Distance Range</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>
                        {range.minDistance} - {range.maxDistance} km
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '4px' }}>Delivery Price</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#4CAF50' }}>
                        ${range.deliveryPrice.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteRange(range.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {showDealModal && <CreateDealModal onClose={() => setShowDealModal(false)} />}

      {showZoneModal && <CreateZoneModal onClose={() => setShowZoneModal(false)} />}

      {showRangeModal && <CreateRangeModal onClose={() => setShowRangeModal(false)} />}
    </div>
  );
};
